import React, {useEffect, useState} from "react";
import {AiConfigDto, ConfigAiConfigRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from "@mui/material";
import {useSnackbar} from "notistack";
import {Page, PageHeader, Panel, SkeletonRows} from "../../../../components/kit";

export const AiConfigPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(ConfigAiConfigRestControllerApiFactory);
    const {enqueueSnackbar} = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<AiConfigDto>({
        enabled: false,
        provider: "ANTHROPIC",
        apiKey: "",
        url: "",
        model: "",
    });

    useEffect(() => {
        apiManager.execute(ctrl.getConfig())
            .then((cfg) => setConfig(cfg as AiConfigDto))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = () => {
        setSaving(true);
        apiManager.execute(ctrl.saveConfig(config), "Configuración guardada")
            .catch(() => enqueueSnackbar("Error al guardar", {variant: "error"}))
            .finally(() => setSaving(false));
    };

    if (loading) return <Page><SkeletonRows rows={4}/></Page>;

    return (
        <Page>
            <PageHeader title="Inteligencia artificial" subtitle="Proveedor y modelo con los que se escanean las facturas recibidas."/>
            <Panel sx={{maxWidth: 640}}>
            <FormControlLabel
                control={
                    <Switch
                        checked={config.enabled ?? false}
                        onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                    />
                }
                label="Activar escaneo de facturas con IA"
                sx={{mb: 2, display: "block"}}
            />

            <FormControl fullWidth sx={{mb: 2}}>
                <InputLabel>Proveedor</InputLabel>
                <Select
                    value={config.provider ?? "ANTHROPIC"}
                    label="Proveedor"
                    onChange={(e) => setConfig({...config, provider: e.target.value})}
                >
                    <MenuItem value="ANTHROPIC">Anthropic (Claude)</MenuItem>
                    <MenuItem value="OPENAI">OpenAI (GPT)</MenuItem>
                    <MenuItem value="OPENROUTER">OpenRouter</MenuItem>
                    <MenuItem value="OLLAMA">Ollama (local)</MenuItem>
                </Select>
            </FormControl>

            <TextField
                fullWidth
                label="API Key"
                type="password"
                value={config.apiKey ?? ""}
                onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                sx={{mb: 2}}
                helperText={config.provider === "OLLAMA" ? "No necesaria para Ollama" : undefined}
            />

            <TextField
                fullWidth
                label="Modelo"
                value={config.model ?? ""}
                onChange={(e) => setConfig({...config, model: e.target.value})}
                sx={{mb: 2}}
                helperText={
                    config.provider === "ANTHROPIC" ? "Ej: claude-haiku-4-5-20251001" :
                    config.provider === "OPENAI"    ? "Ej: gpt-4o-mini" :
                    config.provider === "OPENROUTER" ? "Ej: google/gemma-4-31b-it:free (varios separados por comas = fallback)" :
                                                      "Ej: llama3"
                }
            />

            {config.provider !== "ANTHROPIC" && (
                <TextField
                    fullWidth
                    label={config.provider === "OLLAMA" ? "URL del servidor Ollama" : "URL del endpoint (opcional)"}
                    value={config.url ?? ""}
                    onChange={(e) => setConfig({...config, url: e.target.value})}
                    sx={{mb: 2}}
                    placeholder={
                        config.provider === "OLLAMA" ? "http://localhost:11434" :
                        config.provider === "OPENROUTER" ? "https://openrouter.ai/api/v1/chat/completions" :
                                                           "https://api.openai.com/v1/chat/completions"
                    }
                />
            )}

            <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16}/> : undefined}
            >
                Guardar
            </Button>
            </Panel>
        </Page>
    );
};
